import { supabase } from "src/lib/supabaseClient";
import { fetchUsers, User } from "src/services/usersService";

export interface DashboardStat {
  total: number;
  currentMonth: number;
  previousMonth: number;
}

export interface AdminDashboardStats {
  totalUsers: DashboardStat;
  activeTutors: DashboardStat;
  totalSessions: DashboardStat;
  pendingInquiries: DashboardStat;
}

const getMonthBounds = () => {
  const now = new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  return {
    currentMonthStart: currentMonthStart.toISOString(),
    nextMonthStart: nextMonthStart.toISOString(),
    previousMonthStart: previousMonthStart.toISOString(),
  };
};

const isInRange = (dateString: string | null | undefined, start: string, end: string) => {
  if (!dateString) return false;
  const time = new Date(dateString).getTime();
  return time >= new Date(start).getTime() && time < new Date(end).getTime();
};

const buildUserStat = (users: User[], start: string, end: string, previousStart: string): DashboardStat => ({
  total: users.length,
  currentMonth: users.filter((user) => isInRange(user.created_at, start, end)).length,
  previousMonth: users.filter((user) => isInRange(user.created_at, previousStart, start)).length,
});

const fetchCount = async (
  queryBuilder: PromiseLike<{ count: number | null; error: any }>
): Promise<number> => {
  const { count, error } = await queryBuilder;

  if (error) {
    throw new Error(error.message || "Failed to fetch dashboard count");
  }

  return count || 0;
};

const countSessions = (from?: string, to?: string) => {
  let query = supabase.from("sessions").select("id", { count: "exact", head: true });

  if (from) query = query.gte("scheduled_at", from);
  if (to) query = query.lt("scheduled_at", to);

  return fetchCount(query);
};

const countPendingInquiries = (from?: string, to?: string) => {
  let query = supabase
    .from("inquiries")
    .select("id", { count: "exact", head: true })
    .eq("status", "new");

  if (from) query = query.gte("created_at", from);
  if (to) query = query.lt("created_at", to);

  return fetchCount(query);
};

const countActiveTutors = (from?: string, to?: string) => {
  let query = supabase
    .from("tutors")
    .select("id", { count: "exact", head: true })
    .eq("verification_status", "approved")
    .eq("is_live", true);

  if (from) query = query.gte("created_at", from);
  if (to) query = query.lt("created_at", to);

  return fetchCount(query);
};

export const adminDashboardService = {
  async fetchStats(): Promise<AdminDashboardStats> {
    const { currentMonthStart, nextMonthStart, previousMonthStart } = getMonthBounds();

    const [
      usersResponse,
      activeTutorsTotal,
      activeTutorsCurrentMonth,
      activeTutorsPreviousMonth,
      sessionsTotal,
      sessionsCurrentMonth,
      sessionsPreviousMonth,
      pendingInquiriesTotal,
      pendingInquiriesCurrentMonth,
      pendingInquiriesPreviousMonth,
    ] = await Promise.all([
      fetchUsers(),
      countActiveTutors(),
      countActiveTutors(currentMonthStart, nextMonthStart),
      countActiveTutors(previousMonthStart, currentMonthStart),
      countSessions(),
      countSessions(currentMonthStart, nextMonthStart),
      countSessions(previousMonthStart, currentMonthStart),
      countPendingInquiries(),
      countPendingInquiries(currentMonthStart, nextMonthStart),
      countPendingInquiries(previousMonthStart, currentMonthStart),
    ]);

    return {
      totalUsers: buildUserStat(
        usersResponse.users,
        currentMonthStart,
        nextMonthStart,
        previousMonthStart
      ),
      activeTutors: {
        total: activeTutorsTotal,
        currentMonth: activeTutorsCurrentMonth,
        previousMonth: activeTutorsPreviousMonth,
      },
      totalSessions: {
        total: sessionsTotal,
        currentMonth: sessionsCurrentMonth,
        previousMonth: sessionsPreviousMonth,
      },
      pendingInquiries: {
        total: pendingInquiriesTotal,
        currentMonth: pendingInquiriesCurrentMonth,
        previousMonth: pendingInquiriesPreviousMonth,
      },
    };
  },
};
